/**
 * Mersalg i handlekurven (<cart-upsell>).
 *
 * - Pilknapper for slideren.
 * - Kilde «recommendations»: henter Shopifys anbefalinger for første vare i
 *   handlekurven via seksjonen cart-upsell-recommendations og setter dem inn.
 *   Handlekurven tegnes på nytt (morph) når noe legges til, så elementet
 *   følger med på endringer og setter inn hurtigbufret innhold igjen.
 */
const cache = new Map();

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this, { attributes: true, childList: true, subtree: true });
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    if (this.observer) this.observer.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this.updateArrowsBound);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  get key() {
    const d = this.dataset;
    return [d.productId, d.exclude, d.intent, d.limit].join('|');
  }

  handleClick(event) {
    const button = event.target.closest('[data-cu-prev], [data-cu-next]');
    if (!button) return;
    const track = this.querySelector('[data-cu-track]');
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth;
    track.scrollBy({ left: button.hasAttribute('data-cu-prev') ? -step : step, behavior: 'smooth' });
  }

  sync() {
    if (this.dataset.mode === 'recommendations') {
      const key = this.key;
      if (this.dataset.loadedKey === key) return this.bindTrack();
      if (cache.has(key)) return this.render(key, cache.get(key));
      if (this.loadingKey === key) return;
      this.loadingKey = key;
      this.load(key);
    } else {
      this.bindTrack();
    }
  }

  async load(key) {
    const { productId, intent, limit } = this.dataset;
    if (!productId) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=10&intent=${encodeURIComponent(intent || 'related')}&section_id=cart-upsell-recommendations`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = doc.querySelector('[data-cu-result]');
      const html = result ? result.innerHTML : '';
      cache.set(key, html);
      if (this.key === key) this.render(key, html);
    } catch (error) {
      cache.set(key, '');
      if (this.key === key) this.render(key, '');
    } finally {
      if (this.loadingKey === key) this.loadingKey = null;
    }
  }

  render(key, html) {
    const body = this.querySelector('[data-cu-body]');
    if (!body) return;
    if (body.innerHTML !== html) body.innerHTML = html;
    if (this.dataset.loadedKey !== key) this.dataset.loadedKey = key;
    this.bindTrack();
  }

  bindTrack() {
    const track = this.querySelector('[data-cu-track]');
    const count = track ? Number(track.dataset.count || track.children.length) : 0;
    const shouldHide = count === 0;
    if (this.hidden !== shouldHide) this.hidden = shouldHide;
    if (!track) return;
    if (this.track !== track) {
      if (this.track) this.track.removeEventListener('scroll', this.updateArrowsBound);
      this.track = track;
      this.updateArrowsBound = this.updateArrowsBound || (() => this.updateArrows());
      track.addEventListener('scroll', this.updateArrowsBound, { passive: true });
      // Skuffen kan være lukket (bredde 0) når innholdet settes inn: mål på nytt når den åpnes
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(track);
    }
    requestAnimationFrame(() => this.updateArrows());
  }

  updateArrows() {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    const prev = this.querySelector('[data-cu-prev]');
    const next = this.querySelector('[data-cu-next]');
    if (prev && prev.disabled !== track.scrollLeft <= 2) prev.disabled = track.scrollLeft <= 2;
    if (next && next.disabled !== track.scrollLeft >= max) next.disabled = track.scrollLeft >= max;
    this.classList.toggle('cu--no-scroll', max <= 2);
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
